import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Container } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import HeaderAzul from '~/Components/HeaderAzul/HeaderAzul';
import Loading from '~/Components/Loading';
import {
  gerarRelatorioDespesasAnalitico,
  listarOpcoesRelatorioDespesasAnalitico,
  listarContasGE,
} from '~/Services/Service';
import { exportarEmExcel } from '~/Utils/Helpers';
import { filtrosSchema } from './validationSchema';
import PainelFiltros from './components/PainelFiltros';
import TabelaAgrupada from './components/TabelaAgrupada';
import BotoesAcao from './components/BotoesAcao';

// G-SEL-07: ao limpar, mantém o período (1º dia do mês corrente até hoje, paridade Delphi BitBtn2Click:487-499)
const periodoPadrao = () => {
  const hoje = new Date();
  const ini = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { dataInicio: fmt(ini), dataFim: fmt(hoje) };
};

const INITIAL_VALUES = {
  ...periodoPadrao(),
  grupoEmpresaId: null,
  grupoGeId: null,             // G-SEL-01
  contaId: null,
  aprovadorId: null,
  valorMinimo: '',
  moedaFiltroId: null,
  centroCustoId: null,
  departamentoId: null,
  ocultarContasParametrizadas: false,
  apenasNaoAprovados: false,
  ocultarContasBancos: false,
  incluirContasInternas: false,
  ordenarPorAprovador: false,
};

const EMPTY_OPTIONS = {
  grupos_empresa: [],
  grupos_ge: [],               // G-SEL-01
  moedas: [],
  centros_custo: [],
  departamentos: [],
  contas: [],
  aprovadores: [],
};

const fmtMoeda = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtData = (value) => {
  if (!value) return '';
  const data = new Date(value);
  if (Number.isNaN(data.getTime())) return value;
  return data.toLocaleDateString('pt-BR');
};

const getMensagemErro = (err, fallback) =>
  err && err.message ? err.message : fallback;

const toPayload = (values) => ({
  data_inicio: values.dataInicio,
  data_fim: values.dataFim,
  grupo_empresa_id: values.grupoEmpresaId,
  grupo_ge_id: values.grupoGeId,        // G-SEL-01
  conta_id: values.contaId,
  aprovador_id: values.aprovadorId,
  valor_minimo: values.valorMinimo !== '' ? Number(values.valorMinimo) : null,
  moeda_filtro_id: values.moedaFiltroId,
  centro_custo_id: values.centroCustoId,
  departamento_id: values.departamentoId,
  ocultar_contas_parametrizadas: values.ocultarContasParametrizadas,
  apenas_nao_aprovados: values.apenasNaoAprovados,
  ocultar_contas_bancos: values.ocultarContasBancos,
  incluir_contas_internas: values.incluirContasInternas,
  ordenar_por_aprovador: values.ordenarPorAprovador,
});

// G-COL-06: Excel também separa Débito/Crédito + colunas extras (C.P., C.C./Depto., Lançador)
const montarLinhasExcel = (linhas, totais) => {
  const cabecalho = [
    'GE',
    'Tipo',
    'Conta',
    'Ativo',
    'Saldo Atual',
    'Data',
    'Histórico',
    'Obs Extras',
    'C.P.',
    'Lançador',
    'Aprovador',
    'Débito',
    'Crédito',
    'Conv.',
    'MO 11',
    'C.C./Depto.',
    'Lançamento',
  ];

  const rotuloTipo = (t) => (t === 0 ? 'Despesas/Receitas' : t === 1 ? 'Projeto' : 'Carteira');

  const dados = (linhas || []).map((linha) => {
    const debito =
      linha.codigo_conta_debitada === linha.codigo_conta &&
      linha.codigo_grupo_empresa_deb === linha.codigo_grupo_empresa;
    const obsExtras = [linha.observacao1, linha.observacao2, linha.observacao3]
      .filter((o) => o && o.trim())
      .join(' | ');
    return [
      linha.grupo_empresa || '',
      rotuloTipo(linha.tipo),
      linha.conta || '',
      `${linha.codigo_ativo || ''} ${linha.ativo || ''}`.trim(),
      fmtMoeda(linha.saldo),
      fmtData(linha.data_mata),
      linha.observacao || '',
      obsExtras,
      linha.contrapartida || '',
      linha.nome_usuario || '',
      linha.aprovador || '',
      debito ? fmtMoeda(linha.valor) : '',
      !debito ? fmtMoeda(linha.valor) : '',
      fmtMoeda(linha.valor_convertido),
      fmtMoeda(linha.valor_moeda_11),
      linha.centro_custo_departamento || '',
      linha.codigo_mata || '',
    ];
  });

  const totalGeral =
    totais && totais.total_geral != null ? totais.total_geral : 0;
  const totalConvertido =
    totais && totais.total_convertido != null ? totais.total_convertido : 0;
  const totalMoeda11 =
    totais && totais.total_moeda_11 != null ? totais.total_moeda_11 : 0;
  const colsTotal = cabecalho.length;

  const linhaTotal = Array(colsTotal).fill('');
  linhaTotal[6] = 'TOTAL GERAL';      // Histórico
  linhaTotal[11] = fmtMoeda(totalGeral);   // Débito (acumulado)
  linhaTotal[13] = fmtMoeda(totalConvertido); // Conv.
  linhaTotal[14] = fmtMoeda(totalMoeda11);    // MO 11

  return [
    cabecalho,
    ...dados,
    linhaTotal,
  ];
};

const RelatorioDespesasAnalitico = () => {
  const [opcoes, setOpcoes] = useState(EMPTY_OPTIONS);
  const [loadingOpcoes, setLoadingOpcoes] = useState(true);
  const [loadingContas, setLoadingContas] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [filtrosAtuais, setFiltrosAtuais] = useState(null);

  // Paridade Delphi LgeChange -> abreconta (URelDespesasAnalitico.pas:593-674):
  // contas só carregam após escolher GE, filtradas por tipos 3 e 7 do GE selecionado.
  const handleGrupoEmpresaChange = async (codigoGe) => {
    if (!codigoGe) {
      setOpcoes((prev) => ({ ...prev, contas: [] }));
      return;
    }
    setLoadingContas(true);
    try {
      const response = await listarContasGE(codigoGe, { tipos: '3,7' });
      const lista = Array.isArray(response) ? response : [];
      const contas = lista.map((c) => ({
        codigo: c.codigo_conta,
        descricao: `${c.codigo_conta} - ${c.apelido || ''}`.trim(),
      }));
      setOpcoes((prev) => ({ ...prev, contas }));
    } catch (err) {
      toast.error(getMensagemErro(err, 'Erro ao carregar contas do Grupo de Empresa.'));
      setOpcoes((prev) => ({ ...prev, contas: [] }));
    } finally {
      setLoadingContas(false);
    }
  };

  useEffect(() => {
    let ativo = true;
    const carregarOpcoes = async () => {
      setLoadingOpcoes(true);
      try {
        const response = await listarOpcoesRelatorioDespesasAnalitico();
        const dados = response || EMPTY_OPTIONS;
        if (!ativo) return;
        setOpcoes({
          grupos_empresa: dados.grupos_empresa || [],
          grupos_ge: dados.grupos_ge || [],          // G-SEL-01
          moedas: dados.moedas || [],
          centros_custo: dados.centros_custo || [],
          departamentos: dados.departamentos || [],
          contas: dados.contas || [],
          aprovadores: dados.aprovadores || [],
        });
      } catch (err) {
        if (ativo) {
          toast.error(getMensagemErro(err, 'Erro ao carregar opções de filtro.'));
        }
      } finally {
        if (ativo) setLoadingOpcoes(false);
      }
    };
    carregarOpcoes();
    return () => {
      ativo = false;
    };
  }, []);

  const linhas = useMemo(() => {
    if (!resultado || !resultado.linhas) return [];
    return resultado.linhas;
  }, [resultado]);

  const totais = resultado && resultado.totais ? resultado.totais : {};
  const temResultado = linhas.length > 0;

  const handleGerar = async (values, { setSubmitting }) => {
    setLoading(true);
    setResultado(null);

    const payload = toPayload(values);
    try {
      const response = await gerarRelatorioDespesasAnalitico(payload);
      const dados = response || { linhas: [], totais: {} };
      setResultado(dados);
      setFiltrosAtuais(payload);

      if (!dados.linhas || dados.linhas.length === 0) {
        toast.info('Nenhuma informação encontrada.');
      }
    } catch (err) {
      toast.error(getMensagemErro(err, 'Erro ao gerar relatório.'));
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleLimpar = (resetForm) => {
    // G-SEL-07: paridade Delphi BitBtn2Click — preserva o período (1º dia do mês até hoje)
    setResultado(null);
    setFiltrosAtuais(null);
    resetForm({ values: { ...INITIAL_VALUES, ...periodoPadrao() } });
  };

  const handleExportarExcel = () => {
    if (!temResultado) return;
    exportarEmExcel(
      montarLinhasExcel(linhas, totais),
      `despesas-analitico-${filtrosAtuais.data_inicio}-${filtrosAtuais.data_fim}.csv`
    );
  };

  const handleExportarPdf = () => {
    if (!temResultado) return;
    const pdf = new jsPDF('l', 'mm', 'a4');
    pdf.setFontSize(12);
    pdf.text('Analitico de Despesas', 10, 10);
    pdf.setFontSize(8);

    let y = 18;
    linhas.slice(0, 80).forEach((linha) => {
      const texto = [
        linha.grupo_empresa || '',
        linha.conta || '',
        fmtData(linha.data_mata),
        linha.codigo_mata || '',
        fmtMoeda(linha.valor_convertido),
        fmtMoeda(linha.valor_moeda_11),
      ].join(' | ');
      pdf.text(texto.substring(0, 180), 10, y);
      y += 5;
      if (y > 190) {
        pdf.addPage();
        y = 12;
      }
    });

    pdf.save(`despesas-analitico-${filtrosAtuais.data_inicio}-${filtrosAtuais.data_fim}.pdf`);
  };

  if (loadingOpcoes) {
    return (
      <Container fluid className="px-3 py-3">
        <HeaderAzul title="Analítico de Despesas" />
        <Loading label="Carregando opções..." />
      </Container>
    );
  }

  return (
    <Container fluid className="px-3 py-3">
      <HeaderAzul title="Analítico de Despesas" />
      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={filtrosSchema}
        onSubmit={handleGerar}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting, submitForm, resetForm }) => (
          <Form>
            <PainelFiltros
              values={values}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
              opcoes={opcoes}
              isSubmitting={isSubmitting || loading}
              loadingContas={loadingContas}
              onGrupoEmpresaChange={(geId) => {
                setFieldValue('contaId', null);
                handleGrupoEmpresaChange(geId);
              }}
            />
            <BotoesAcao
              onGerar={submitForm}
              onLimpar={() => handleLimpar(resetForm)}
              onExportarExcel={handleExportarExcel}
              onExportarPdf={handleExportarPdf}
              loading={loading || isSubmitting}
              temResultado={temResultado}
            />
            {loading && <Loading label="Gerando relatório..." />}
            {!loading && resultado && !temResultado && (
              <Alert variant="info">Nenhuma informação encontrada.</Alert>
            )}
            {/* G-COL-11: cabeçalho com filtros aplicados (paridade Delphi pas:159-298 Periodo.Caption) */}
            {!loading && temResultado && filtrosAtuais && (
              <Alert variant="light" className="border mt-3 mb-2 py-2 small">
                <strong>Período:</strong> {fmtData(filtrosAtuais.data_inicio)} a {fmtData(filtrosAtuais.data_fim)}
                {filtrosAtuais.grupo_empresa_id != null && opcoes.grupos_empresa.find((g) => g.codigo === filtrosAtuais.grupo_empresa_id) && (
                  <> {' | '} <strong>GE:</strong> {opcoes.grupos_empresa.find((g) => g.codigo === filtrosAtuais.grupo_empresa_id).descricao}</>
                )}
                {filtrosAtuais.grupo_ge_id != null && opcoes.grupos_ge.find((g) => g.codigo === filtrosAtuais.grupo_ge_id) && (
                  <> {' | '} <strong>Grupo de GE:</strong> {opcoes.grupos_ge.find((g) => g.codigo === filtrosAtuais.grupo_ge_id).descricao}</>
                )}
                {filtrosAtuais.moeda_filtro_id != null && opcoes.moedas.find((m) => m.codigo === filtrosAtuais.moeda_filtro_id) && (
                  <> {' | '} <strong>Moeda:</strong> {opcoes.moedas.find((m) => m.codigo === filtrosAtuais.moeda_filtro_id).descricao}</>
                )}
                {filtrosAtuais.centro_custo_id != null && opcoes.centros_custo.find((c) => c.codigo === filtrosAtuais.centro_custo_id) && (
                  <> {' | '} <strong>C.C.:</strong> {opcoes.centros_custo.find((c) => c.codigo === filtrosAtuais.centro_custo_id).descricao}</>
                )}
                {filtrosAtuais.departamento_id != null && opcoes.departamentos.find((d) => d.codigo === filtrosAtuais.departamento_id) && (
                  <> {' | '} <strong>Depto.:</strong> {opcoes.departamentos.find((d) => d.codigo === filtrosAtuais.departamento_id).descricao}</>
                )}
                {filtrosAtuais.aprovador_id != null && opcoes.aprovadores.find((a) => a.codigo === filtrosAtuais.aprovador_id) && (
                  <> {' | '} <strong>Aprovador:</strong> {opcoes.aprovadores.find((a) => a.codigo === filtrosAtuais.aprovador_id).descricao}</>
                )}
              </Alert>
            )}
            {!loading && temResultado && (
              <TabelaAgrupada linhas={linhas} totais={totais} />
            )}
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default RelatorioDespesasAnalitico;
